import React from 'react';
import {
  Marker as LeafletMarker,
  Popup as LeafletPopup,
  } from 'react-leaflet';
import L from 'leaflet';
import * as Utils from './utils';
import './cluster.scss';

/** Cluster Component
 * required:
 * @param cluster: {markers: [<Marker>], center: [LatLng], bounds{LatLngBounds}}
 * optional:
 * @param icons: {}, see parent description
 * @param clusterFunction
 * @return  React component to be rendered for each marker and cluster
 */
class Cluster extends React.Component {
  constructor() {
    super();
    this.clusterIconStyle = null;

    this.handleClusterClick = this.handleClusterClick.bind(this);
    this.getClusterValue = this.getClusterValue.bind(this);
    this.handleExitFullScreenClick = this.handleExitFullScreenClick.bind(this);
  }

  handleExitFullScreenClick(type) {
    if (document.exitFullscreen) {
           document.exitFullscreen();
       } else if (document.webkitExitFullscreen) {
           document.webkitExitFullscreen();
       } else if (document.mozCancelFullScreen) {
           document.mozCancelFullScreen();
       } else if (document.msExitFullscreen) {
           document.msExitFullscreen();
       }
  }

  getMarker(cluster) {
    const marker = cluster.markers[0];

    // render div icon if css class is provided else use icon_url
    let markerIcon =
      this.props.icons.marker_icon_class ? L.divIcon({ className: `${this.props.icons.marker_icon_class} mt-marker-icon`, html: marker.value }) : L.icon({ className: 'mt-leaflet-marker-icon', iconUrl: this.props.icons.marker_icon_url || '' });

    let linkWrapperBefore = '';
    let linkWrapperAfter = '';
    if (this.props.context === 'lity') {
      linkWrapperBefore = `<a onClick={() => this.handleExitFullScreenClick()} data-lity href="${marker.link}">`;
      linkWrapperAfter = '</a>';
    }

    if (this.props.propertyKey === 'thumbnail') {
      markerIcon = this.props.icons.marker_icon_class ? L.divIcon({ className: `${this.props.icons.marker_icon_class} mt-marker-icon`, html: `${linkWrapperBefore}<div class="mt-zoom-container maptool-scale-80"><div class="button--middle-center text--center maptool-play-button play-button play-button--small"></div><img src="${marker.value}" class="mt-thumbnail relative maptool-border-white"></div>${linkWrapperAfter}` }) : L.icon({ className: 'mt-leaflet-marker-icon', iconUrl: this.props.icons.marker_icon_url || '' });
    }

    const link = this.props.context === 'lity' ? <a onClick={() => this.handleExitFullScreenClick()} data-lity href={marker.link}>{cluster.markers[0].name}</a> : <a href={marker.link}>{cluster.markers[0].name}</a>;

    // marker obj has a link property, render link into popup, else just span with city name
    const popup =
      <LeafletPopup minWidth={120} className="mt-leaflet-popup">{marker.link !== '' ? link : <span dangerouslySetInnerHTML={{ __html: marker.name }}></span>}</LeafletPopup>;

    return (
      <span>
        <LeafletMarker position={marker.position} icon={markerIcon}>
           {this.props.propertyKey !== 'thumbnail' ? popup : ''}
        </LeafletMarker>
      </span>
    );
  }

  // value for cluster icon, default length of markers array
  getClusterValue(cluster, functionName, precision = 0) {
    if (typeof functionName !== 'undefined' && functionName !== '') {
      const featureVals = cluster.markers.map(marker => marker.value);
      return Utils[functionName](featureVals, precision);
    }
    return cluster.markers.length;
  }

  getClusterUnique(cluster) {
    const s = cluster.markers.map((marker) => marker.value);
    return s.join();
  }


  // paint livecams imploded cluster
  paintImplodedCluster(cluster) {

    let counter = null;
    if (cluster.markers.length > 1) {
      counter = ( <div className="spider-counter"> {cluster.markers.length} </div>);
    }

    const marker = cluster.markers[0];
    const style = {backgroundImage: 'url("'+marker.value+'")' };

    const content = (
      <div>
        <div className="mt-zoom-container">
          <div className="spider-center"></div>
          <div className="spider-rect spider-single" key={`SingleItem_${marker.link}`}></div>
          <div className="spieder-arrow"></div>
          <div className="spider-rect spider-single spider-image" key={`SingleItemImage_${marker.link}`} style={style}></div>
        </div>
        {counter}
      </div>
    );


    if (cluster.markers.length > 1) {
      return (
        <div onClick={() => this.handleClusterClick(cluster)}>{content}</div>
      );
    } else {
      return (
        <div onClick={() => this.handleExitFullScreenClick()}>
          <a
            data-lity
            href={marker.link}
            key={`SingleLink_${marker.link}`}
            data-lity-target={`${marker.source}`}
          >
            {content}
          </a>
        </div>
      );
    }

  }


  // paint spider cluster
  paintSpiderCluster(cluster) {
    let radius = 80;
    const centerX = 0;
    const centerY = 0;
    const distance = 1;

    if (cluster.markers.length > 4) {
      radius = 100;
    }

    const space = 360 / cluster.markers.length;
    let angle = 20;
    const ms = cluster.markers.map((marker) => {
      angle = angle + space;
      const x = parseInt(centerX + radius * Math.cos(-angle * Math.PI / 180) * distance, 10);
      const y = parseInt(centerY + radius * Math.sin(-angle * Math.PI / 180) * distance, 10);
      const item = {
        marker,
        x,
        y,
        style: { left: x+'px', top: y+'px', backgroundImage: 'url("'+marker.value+'")' }, // eslint-disable-line
        linestyle: { transform: 'rotate('+(angle*-1)+'deg)'}, // eslint-disable-line
      };
      return item;
    });

    const spiderparts = ms.map((item) => (
      <a onClick={() => this.handleExitFullScreenClick()} data-lity href={item.marker.link} key={`SpiderItem_${item.marker.link}`}>
        <div
          className="spider-rect"
          key={`SpiderPicture_${item.marker.link}`}
          style={item.style}
        >
          <div
            className="spider-playbutton button--middle-center text--center play-button play-button--small "
            key={`SpiderPlayButton_${item.marker.link}`}
          >
          </div>
        </div>
        <div
          className="spider-line"
          key={`SpiderLine_${item.marker.link}`}
          style={item.linestyle}
        >
        </div>
      </a>
    ));

    return (
      <div>
        <div className="spider-center"></div>
        {spiderparts}
      </div>
    );
  } // paintSpiderCluster

  // increase zoomlevel after clicking cluster icon
  handleClusterClick(cluster) {
    this.props.map.fitBounds(cluster.bounds);
    const unique = this.getClusterUnique(cluster);
    this.props.selectHandlerExplodedView(unique);
  }

  render() {
    const cluster = this.props.cluster;

    const unique = this.getClusterUnique(cluster);
    let isExplodedView = false;
    if (unique === this.props.explodedViewID) {
      isExplodedView = true;
    }

    if (this.props.propertyKey === 'thumbnail') {
      if (isExplodedView) {
        return this.paintSpiderCluster(cluster);
      } else {// eslint-disable-line
        return this.paintImplodedCluster(cluster);
      }
    } else {
      // render marker with popup
      if (cluster.markers.length === 1) {
        return this.getMarker(cluster);
      } else {
        return (
          <div
            style={this.clusterIconStyle}
            className={this.props.icons.cluster_icon_class || 'mt-cluster-icon'}
            onClick={() => this.handleClusterClick(cluster)}
          >
            {this.getClusterValue(cluster, this.props.clusterFunction, this.props.precision)}
          </div>
        );
      }
    }


  }
}

Cluster.defaultProps = {
  cluster_icon_url: 'http://ls2.wettercomassets.com/wcomv5/images/icon_pin_black_shadow_cluster.png',
  marker_icon_url: 'http://ls2.wettercomassets.com/wcomv5/images/icon_pin_black_shadow.png'
};

export default Cluster;



/** WEBPACK FOOTER **
 ** ./core_components/DataLayer/MarkerClusterLayer/Cluster.js
 **/